import { useState, useEffect } from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface OutlineSection {
  id: string;
  name: string;
  included: boolean;
}

interface OutlineBuilderModalProps {
  isOpen: boolean;
  paperType: string;
  onConfirm: (sections: OutlineSection[]) => void;
  onCancel: () => void;
}

// Default outlines for different paper types
const DEFAULT_OUTLINES: Record<string, string[]> = {
  'Research Paper': ['Introduction', 'Literature Review', 'Methodology', 'Results', 'Discussion', 'Conclusion'],
  'Essay': ['Introduction', 'Background', 'Main Arguments', 'Counter-Arguments', 'Analysis', 'Conclusion'],
  'Thesis': ['Introduction', 'Literature Review', 'Theoretical Framework', 'Methodology', 'Results and Analysis', 'Discussion', 'Implications', 'Conclusion'],
  'Dissertation': ['Introduction', 'Literature Review', 'Theoretical Framework', 'Research Design', 'Methodology', 'Data Analysis', 'Findings', 'Discussion', 'Implications', 'Limitations', 'Conclusion'],
  'Literature Review': ['Introduction', 'Search Methodology', 'Thematic Analysis', 'Key Findings', 'Research Gaps', 'Synthesis', 'Conclusion'],
  'Case Study': ['Introduction', 'Background', 'Case Description', 'Analysis', 'Findings', 'Discussion', 'Recommendations', 'Conclusion'],
  'Report': ['Executive Summary', 'Introduction', 'Background', 'Findings', 'Analysis', 'Recommendations', 'Conclusion'],
  'Article': ['Introduction', 'Background', 'Main Content', 'Discussion', 'Implications', 'Conclusion'],
};

// Sortable section row component
function SortableSection({ section, onToggle, onEdit, onDelete }: {
  section: OutlineSection;
  onToggle: () => void;
  onEdit: (name: string) => void;
  onDelete: () => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(section.name);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleSaveEdit = () => {
    if (editValue.trim()) {
      onEdit(editValue.trim());
      setIsEditing(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      setEditValue(section.name);
      setIsEditing(false);
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 rounded-xl bg-white border transition-all ${
        section.included ? 'border-gray-200 hover:border-emerald-300' : 'border-gray-100 bg-gray-50 opacity-60'
      }`}
    >
      {/* Drag Handle */}
      <button
        {...attributes}
        {...listeners}
        className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing flex-shrink-0"
      >
        <i className="ri-draggable text-base"></i>
      </button>

      {/* Toggle Checkbox */}
      <div
        onClick={onToggle}
        className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-all cursor-pointer flex-shrink-0 ${
          section.included ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300'
        }`}
      >
        {section.included && <i className="ri-check-line text-white text-xs"></i>}
      </div>

      {/* Section Name */}
      {isEditing ? (
        <input
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSaveEdit}
          onKeyDown={handleKeyDown}
          autoFocus
          className="flex-1 px-2 py-1 text-sm font-semibold text-gray-800 border border-emerald-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400"
        />
      ) : (
        <button
          onClick={() => setIsEditing(true)}
          className="flex-1 text-left text-sm font-semibold text-gray-800 hover:text-emerald-600 transition-colors"
        >
          {section.name}
        </button>
      )}

      {/* Delete Button */}
      <button
        onClick={onDelete}
        className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-all cursor-pointer flex-shrink-0"
      >
        <i className="ri-delete-bin-line text-sm"></i>
      </button>
    </div>
  );
}

export default function OutlineBuilderModal({ isOpen, paperType, onConfirm, onCancel }: OutlineBuilderModalProps) {
  const [sections, setSections] = useState<OutlineSection[]>([]);
  const [newSectionName, setNewSectionName] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Initialize sections when modal opens or paper type changes
  useEffect(() => {
    if (isOpen) {
      const defaultSections = DEFAULT_OUTLINES[paperType] || DEFAULT_OUTLINES['Research Paper'];
      setSections(
        defaultSections.map((name, index) => ({
          id: `section-${index}-${Date.now()}`,
          name,
          included: true,
        }))
      );
    }
  }, [isOpen, paperType]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setSections((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const toggleSection = (id: string) => {
    setSections((prev) =>
      prev.map((s) => (s.id === id ? { ...s, included: !s.included } : s))
    );
  };

  const editSection = (id: string, name: string) => {
    setSections((prev) =>
      prev.map((s) => (s.id === id ? { ...s, name } : s))
    );
  };

  const deleteSection = (id: string) => {
    setSections((prev) => prev.filter((s) => s.id !== id));
  };

  const addSection = () => {
    if (newSectionName.trim()) {
      setSections((prev) => [
        ...prev,
        {
          id: `section-${Date.now()}-${Math.random()}`,
          name: newSectionName.trim(),
          included: true,
        },
      ]);
      setNewSectionName('');
    }
  };

  const restoreDefaults = () => {
    const defaultSections = DEFAULT_OUTLINES[paperType] || DEFAULT_OUTLINES['Research Paper'];
    setSections(
      defaultSections.map((name, index) => ({
        id: `section-${index}-${Date.now()}`,
        name,
        included: true,
      }))
    );
  };

  const handleConfirm = () => {
    onConfirm(sections.filter((s) => s.included));
  };

  const includedCount = sections.filter((s) => s.included).length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-8 py-6 bg-gradient-to-r from-emerald-600 to-teal-600 border-b border-emerald-700">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/20 flex-shrink-0">
                <i className="ri-list-ordered text-white text-lg"></i>
              </div>
              <div>
                <h2 className="text-lg font-extrabold text-white">Customize Your Outline</h2>
                <p className="text-xs text-emerald-100">Drag to reorder · Click to edit · Toggle to include/exclude</p>
              </div>
            </div>
            <button
              onClick={onCancel}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/20 text-white transition-all cursor-pointer flex-shrink-0"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold px-3 py-1 rounded-full bg-white/20 text-white border border-white/30">
              {paperType}
            </span>
            <span className="text-xs font-semibold px-3 py-1 rounded-full bg-white/20 text-white border border-white/30">
              {includedCount} section{includedCount !== 1 ? 's' : ''} included
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Instructions */}
          <div className="flex items-start gap-3 p-4 rounded-xl bg-emerald-50 border border-emerald-200">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-100 flex-shrink-0">
              <i className="ri-information-line text-emerald-600 text-base"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-emerald-900 mb-1">How to customize your outline:</p>
              <ul className="text-xs text-emerald-700 space-y-0.5 leading-relaxed">
                <li>• <strong>Drag</strong> the handle icon to reorder sections</li>
                <li>• <strong>Click</strong> section names to edit them inline</li>
                <li>• <strong>Toggle</strong> checkboxes to include or exclude sections</li>
                <li>• <strong>Delete</strong> sections you don't need</li>
              </ul>
            </div>
          </div>

          {/* Sections List */}
          <div className="space-y-2">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">Paper Sections</p>
              <button
                onClick={restoreDefaults}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 text-gray-600 text-xs font-bold hover:bg-gray-200 transition-all cursor-pointer"
              >
                <i className="ri-refresh-line text-sm"></i>
                Restore Defaults
              </button>
            </div>

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-2">
                  {sections.map((section) => (
                    <SortableSection
                      key={section.id}
                      section={section}
                      onToggle={() => toggleSection(section.id)}
                      onEdit={(name) => editSection(section.id, name)}
                      onDelete={() => deleteSection(section.id)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>

          {/* Add Section */}
          <div className="pt-2">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Add Custom Section</p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newSectionName}
                onChange={(e) => setNewSectionName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addSection()}
                placeholder="Enter section name..."
                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400 transition-all"
              />
              <button
                onClick={addSection}
                disabled={!newSectionName.trim()}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
              >
                <i className="ri-add-line text-base"></i>
                Add
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between gap-3">
          <button
            onClick={onCancel}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white border border-gray-200 text-gray-700 text-sm font-bold hover:bg-gray-100 transition-all cursor-pointer"
          >
            <i className="ri-close-line text-base"></i>
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={includedCount === 0}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-sm font-bold hover:shadow-lg transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed shadow-md"
          >
            <i className="ri-magic-line text-base"></i>
            Generate Paper with This Outline
          </button>
        </div>
      </div>
    </div>
  );
}